import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import api from '../../services/api'
import { hasPermission } from '../../utils/hasPermission'
import {
  USER_PERMISSIONS,
  FLOW_MONITOR_ALERT_TYPE_LABELS,
} from '../../constants'

import { H3 } from '../common/Headers'
import { Spin, Switch, AntSelect } from '../common/Ant'
import { Option } from '../common/Select'
import { ActionAnchor } from '../common/Anchor'
import Button from '../common/Button'

const FlowMonitorAlertsSettings = ({ siteSlug, siteUsers }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [recipients, setRecipients] = useState([])
  const [subscriptions, setSubscriptions] = useState([])
  const [selectedUserId, setSelectedUserId] = useState(undefined)

  const canEdit = hasPermission(USER_PERMISSIONS.EDIT_FLOW_MONITOR)

  const getSettings = useCallback(() => {
    api
      .getFlowMonitorAlertSettings(siteSlug)
      .then(data => {
        setRecipients(data.recipients)
        setSubscriptions(data.subscriptions)
        setIsLoading(false)
      })
      .catch(e => {
        console.error(e)
        setIsLoading(false)
      })
  }, [siteSlug])

  useEffect(() => {
    getSettings()
  }, [siteSlug, getSettings])

  const handleAddRecipient = () => {
    if (!selectedUserId) return

    api
      .addFlowMonitorAlertRecipient(siteSlug, selectedUserId)
      .then(() => {
        setSelectedUserId(undefined)
        getSettings()
      })
      .catch(e => console.error(e))
  }

  const handleRemoveRecipient = recipientId => {
    api
      .removeFlowMonitorAlertRecipient(siteSlug, recipientId)
      .then(() => getSettings())
      .catch(e => console.error(e))
  }

  const handleToggleSubscription = (alertType, active) => {
    api
      .updateFlowMonitorAlertSubscription(siteSlug, alertType, active)
      .then(() => getSettings())
      .catch(e => console.error(e))
  }

  if (isLoading) {
    return <Spin size="large" className="w-100 center mv5" />
  }

  const availableUsers = siteUsers.filter(
    user => !recipients.some(recipient => recipient.userId === user.id)
  )

  return (
    <div className="pa black-80">
      <div className="mb3 ba b--black-10 br3 pa3 bg-white shadow-4">
        <H3 className="ma0 mb2 f4">Recipients</H3>
        <ul className="list pl0 mb3">
          {recipients.map(recipient => (
            <li
              key={recipient.id}
              className="flex justify-between items-center pv1 bb b--black-10"
            >
              <span>
                {recipient.userName} ({recipient.userEmail})
              </span>
              {canEdit && (
                <ActionAnchor
                  onClick={() => handleRemoveRecipient(recipient.id)}
                >
                  Remove
                </ActionAnchor>
              )}
            </li>
          ))}
          {recipients.length === 0 && (
            <li className="black-60">No recipients configured</li>
          )}
        </ul>
        {canEdit && (
          <div className="flex items-center">
            <AntSelect
              className="w-40 mr2"
              showSearch
              allowClear
              placeholder="Select a user"
              value={selectedUserId}
              onChange={setSelectedUserId}
              optionFilterProp="children"
            >
              {availableUsers.map(user => (
                <Option value={user.id} key={user.id}>
                  {user.firstName} {user.lastName}
                </Option>
              ))}
            </AntSelect>
            <Button
              text="Add Recipient"
              onClick={handleAddRecipient}
              disabled={!selectedUserId}
            />
          </div>
        )}
      </div>

      <div className="mb3 ba b--black-10 br3 pa3 bg-white shadow-4">
        <H3 className="ma0 mb2 f4">Alerts</H3>
        {subscriptions.map(subscription => (
          <div
            key={subscription.alertType}
            className="flex justify-between items-center pv1 bb b--black-10"
          >
            <span>
              {FLOW_MONITOR_ALERT_TYPE_LABELS[subscription.alertType] ||
                subscription.alertType}
            </span>
            <Switch
              checked={subscription.active}
              disabled={!canEdit}
              onChange={checked =>
                handleToggleSubscription(subscription.alertType, checked)
              }
            />
          </div>
        ))}
      </div>
    </div>
  )
}

FlowMonitorAlertsSettings.propTypes = {
  siteSlug: PropTypes.string.isRequired,
  siteUsers: PropTypes.arrayOf(PropTypes.object).isRequired,
}

export default FlowMonitorAlertsSettings
